import { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuthStore } from '../../store/authStore';
import { formatPrice } from '../../lib/regionConfig';
import { API_URL } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';

const STATUSES = [
  { value: 'trialing', label: 'Trialing' },
  { value: 'active', label: 'Active (paid)' },
  { value: 'canceled', label: 'Canceled' },
  { value: 'expired', label: 'Expired' },
  { value: 'past_due', label: 'Past due' },
];

const PRESETS = [
  { label: 'Expired trial', form: { subscription_status: 'expired', subscription_plan: 'free_trial', days_until_trial_ends: -1 } },
  { label: 'Trial ending soon', form: { subscription_status: 'trialing', subscription_plan: 'free_trial', days_until_trial_ends: 3 } },
  { label: 'Cancelled, ending', form: { subscription_status: 'active', subscription_plan: 'founding_member', cancel_at_period_end: true, days_until_period_end: 10 } },
  { label: 'Active subscriber', form: { subscription_status: 'active', subscription_plan: 'founding_member' } },
];

const BLANK = { days_until_trial_ends: null, cancel_at_period_end: false, days_until_period_end: null };

/** Set a user's subscription by hand, to see the app in each state. For testing. */
export function SubscriptionOverrideDialog({ user, onOpenChange, onChanged }) {
  const getSubscriptionPlans = useAuthStore((s) => s.getSubscriptionPlans);
  const [plans, setPlans] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getSubscriptionPlans().then((r) => { if (!cancelled && r.success) setPlans(r.data.plans); });
    return () => { cancelled = true; };
  }, [getSubscriptionPlans]);

  useEffect(() => {
    if (!user) return;
    setForm({
      ...BLANK,
      subscription_status: user.subscription_status || 'trialing',
      subscription_plan: user.subscription_plan || 'free_trial',
      days_until_trial_ends: 30,
      cancel_at_period_end: user.cancel_at_period_end || false,
    });
  }, [user]);

  const planLabel = (key, fallback) => {
    const plan = plans?.[key];
    return plan ? `${plan.name} (${formatPrice(plan.price, plan.currency)}/mo)` : fallback;
  };
  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));
  const days = (v) => (v === null || v === '' ? undefined : parseInt(v, 10));

  const apply = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/admin/users/${user.id}/subscription-override`, {
        subscription_status: form.subscription_status,
        subscription_plan: form.subscription_plan,
        days_until_trial_ends: days(form.days_until_trial_ends),
        cancel_at_period_end: form.cancel_at_period_end || undefined,
        days_until_period_end: days(form.days_until_period_end),
      });
      toast.success(`Subscription updated for ${user.email}`);
      onOpenChange(false);
      onChanged();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to override subscription');
    } finally {
      setSaving(false);
    }
  };

  const resetTrial = async () => {
    try {
      await axios.post(`${API_URL}/admin/users/${user.id}/subscription-reset`);
      toast.success(`Subscription reset to a fresh trial for ${user.email}`);
      onOpenChange(false);
      onChanged();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset subscription');
    }
  };

  return (
    <Dialog open={!!user} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Override subscription</DialogTitle>
          <DialogDescription>
            For testing: puts <strong>{user?.email}</strong> in a subscription state without a payment.
          </DialogDescription>
        </DialogHeader>
        {form && (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              {PRESETS.map((p) => (
                <Button key={p.label} variant="outline" size="sm" onClick={() => setForm({ ...BLANK, ...p.form })}>
                  {p.label}
                </Button>
              ))}
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={form.subscription_status} onValueChange={(v) => set({ subscription_status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Plan</Label>
                <Select value={form.subscription_plan} onValueChange={(v) => set({ subscription_plan: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="free_trial">Free trial</SelectItem>
                    <SelectItem value="founding_member">{planLabel('founding_member', 'Solo')}</SelectItem>
                    <SelectItem value="regular">{planLabel('regular', 'Regular')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="override-trial-days">Days until the trial ends</Label>
              <Input
                id="override-trial-days"
                type="number"
                placeholder="30, 7, 0, or -1 for already ended"
                value={form.days_until_trial_ends ?? ''}
                onChange={(e) => set({ days_until_trial_ends: e.target.value })}
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="override-cancel"
                checked={form.cancel_at_period_end}
                onCheckedChange={(v) => set({ cancel_at_period_end: v === true })}
              />
              <Label htmlFor="override-cancel">Cancels at the end of the period</Label>
            </div>
            {form.cancel_at_period_end && (
              <div className="space-y-2">
                <Label htmlFor="override-period-days">Days until the period ends</Label>
                <Input
                  id="override-period-days"
                  type="number"
                  placeholder="15"
                  value={form.days_until_period_end ?? ''}
                  onChange={(e) => set({ days_until_period_end: e.target.value })}
                />
              </div>
            )}
          </div>
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={resetTrial}>Reset to a fresh trial</Button>
          <Button onClick={apply} disabled={saving || !form}>
            {saving && <Loader2 className="animate-spin" />}
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
